import Vue from 'vue';
import buildApiQuery from '@/lib/buildApiQuery';
import kmToMiles from '@/lib/kmToMiles';
import isEqual from 'lodash/isEqual';
import queryString from 'query-string';
import handleErrors from '@/lib/handleErrors';

const moduleState = {
  analyzeShowFilters: true,
  count: 0,
  electricPortCount: 0,
  csv: null,
  currentApiQuery: {},
  analyzeError: null,
};

const moduleGetters = {
  getAnalyzeShowFilters: (state) => state.analyzeShowFilters,
  analyzeCount: (state) => state.count,
  analyzeElectricPortCount: (state) => state.electricPortCount,
  analyzeCsv: (state) => state.csv,
  getAnalyzeError: (state) => state.analyzeError,
};

const mutations = {
  setAnalyzeShowFilters(state, status) {
    state.analyzeShowFilters = status;
  },

  saveAnalyzeCurrentApiQuery(state, apiQuery) {
    state.currentApiQuery = apiQuery;
  },

  saveAnalyzeResults(state, payload) {
    if (payload && payload.data) {
      state.count = payload.data.total_results;
      state.electricPortCount = payload.data.station_counts.fuels.ELEC.total;

      const csvPath = payload.apiQuery.radius ? 'v1/nearest' : 'v1';
      const csvQuery = queryString.stringify({
        ...payload.apiQuery,
        download: 'true',
        limit: 'all',
        // Include UTF8 BOM in the CSV response for better special character
        // handling with Excel (particularly for French data). This assumes
        // most people using the locator will be opening the CSVs in Excel
        // where this is needed to display UTF8 characters properly.
        utf8_bom: 'true',
      });
      state.csv = `${process.env.VUE_APP_API_URL_ROOT}/${csvPath}.csv?${csvQuery}`;
    } else {
      state.count = 0;
      state.electricPortCount = 0;
      state.csv = null;
    }

    if (payload) {
      state.currentApiQuery = payload.apiQuery;
    }
  },

  setAnalyzeError(state, status) {
    state.analyzeError = status;
  },
};

const actions = {
  async fetchAnalyzeResults({ commit, state, getters }, query) {
    try {
      const apiQuery = {
        ...buildApiQuery(getters, query),
        limit: 0,
      };

      // The exact API used depends on whether we're doing an address-based
      // radius filter or a region-based contains filter.
      let apiUrl;
      if (query.location_mode === 'address') {
        apiQuery.location = query.location;
        apiQuery.radius = query.radius;
        apiUrl = `${process.env.VUE_APP_API_URL_ROOT}/v1/nearest.json`;

        if (getters.getFilterValueWithDefault('radius_unit') === 'km') {
          apiQuery.radius = kmToMiles(apiQuery.radius);
        }
      } else {
        apiQuery.state = query.region;
        apiUrl = `${process.env.VUE_APP_API_URL_ROOT}/v1.json`;
      }

      if (isEqual(apiQuery, state.currentApiQuery)) {
        return;
      }

      commit('setLoadingAnalyzeResults', true);
      commit('saveAnalyzeCurrentApiQuery', apiQuery);

      // If the start or end location is empty, then return an empty result without
      // actually performing an ajax query.
      if (query.location_mode === 'address' && (apiQuery.location === undefined || apiQuery.location === null || apiQuery.location === '')) {
        commit('saveAnalyzeResults', {
          apiQuery,
        });
        commit('setLoadingAnalyzeResults', false);
        return;
      }

      const response = await Vue.axios.get(apiUrl, {
        params: apiQuery,
      });
      commit('saveAnalyzeResults', {
        apiQuery,
        data: response.data,
      });
      commit('setAnalyzeError', null);
    } catch (e) {
      commit('saveAnalyzeResults');
      commit('setAnalyzeError', handleErrors(e));
    } finally {
      commit('setLoadingAnalyzeResults', false);
    }
  },
};

export default {
  state: moduleState,
  getters: moduleGetters,
  mutations,
  actions,
};
